import React, {useEffect, useState} from 'react';
import {
    Upload,
    Plus,
    Tag,
    BookMarked,
    DollarSign,
    BookOpen,
    Package,
    Wrench,
    List,
    Download,
    Wheat
} from 'lucide-react';
import {Link, useLocation, useSearchParams} from "react-router-dom";
import Categories from './category/categories';
import RemiseCategories from './discount/RemiseCategories';
import BrandsList from "./brand/BrandsList";
import ProductList from "./product/ProductList";
import MenuPage from "./menu/MenuPage";
import secureStorage from "../../utils/secureStorage";
import {api} from "../../config/api";
import {tabClass, tabIconClass} from '../Common/TabBar';
import Button from '../Common/Button';
import Notification from "../Common/Notification";


const CatalogPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const [selectedFile, setSelectedFile] = useState(null);
    const [menuCreateRequestKey, setMenuCreateRequestKey] = useState(0);

    const VALID_TABS = ['articles', 'produits', 'prestations', 'ingredients', 'categories', 'brands', 'remises', 'menus'];
    const activeTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'articles';

    const setActiveTab = (tab) => {
        setSearchParams(prev => {
            // Keep only 'tab', drop all product list params when switching tabs
            return new URLSearchParams({tab});
        }, {replace: false});
    };

    const [isPopupVisible, setPopupVisible] = useState(false);
    const [step, setStep] = useState(1);
    const [importOption, setImportOption] = useState('modify');


    const [importErrors, setImportErrors] = useState([]);
    const [exportedFileUrl, setExportedFileUrl] = useState(null);

    const handleOpenPopup = () => setPopupVisible(true);

    const handleClosePopup = () => {
        setPopupVisible(false);
        setStep(1);
        setImportErrors([]);
        setExportedFileUrl(null);
    };

    const [notification, setNotification] = useState({show: false, type: 'success', message: ''});

    const showNotification = (type, message) => {
        setNotification({show: true, type, message});
        if (type !== 'loading') {
            setTimeout(() => setNotification(prev => ({...prev, show: false})), 5000);
        }
    };

    const [isRestaurant, setIsRestaurant] = useState(false);

    // Check once if this is a restaurant account (busniessType === "004")
    useEffect(() => {
        secureStorage.getItem('selectedAccount').then(acc => {
            setIsRestaurant(acc?.busniessType === '004');
        });
    }, []);

    const hideNotification = () => setNotification(prev => ({...prev, show: false}));

    const handleImportFile = async () => {
        if (!selectedFile) {
            showNotification("error", "Aucun fichier sélectionné");
            return;
        }

        setImportErrors([]);

        try {
            showNotification("loading", "Importation en cours...");

            const accountId = await secureStorage.getItem("selectedAccount");
            const currentDate = new Date().toISOString();

            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append("importOption", importOption);

            const response = await api.post("/catalogue/import-excel", formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'multipart/form-data',
                    'X-last-request': currentDate,
                }
            });

            if (response.data && response.status === 200) {
                const lineErrors = Array.isArray(response.data.errors) ? response.data.errors : [];

                if (lineErrors.length > 0) {
                    setImportErrors(lineErrors);
                    showNotification("error", response.data.message || "Catalogue importé avec des erreurs, voir le détail ci-dessous");
                } else {
                    showNotification("success", "Catalogue importé avec succès");
                    setSelectedFile(null);
                    if (document.getElementById('catalog-file-input')) {
                        document.getElementById('catalog-file-input').value = '';
                    }
                    handleClosePopup();
                }
            } else {
                showNotification("error", "Erreur lors de l'importation du catalogue");
            }
        } catch (error) {
            console.error("Error importing file:", error);
            let errorMessage = "Une erreur est survenue lors de l'importation du catalogue";

            if (error.response) {
                if (error.response.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else if (error.response.status === 413) {
                    errorMessage = "Le fichier est trop volumineux";
                } else if (error.response.status === 400) {
                    errorMessage = "Format de fichier invalide ou données incorrectes";
                } else if (error.response.status === 401) {
                    errorMessage = "Vous n'êtes pas autorisé à effectuer cette opération";
                }

                if (Array.isArray(error.response.data?.errors)) {
                    setImportErrors(error.response.data.errors);
                }
            } else if (error.request) {
                errorMessage = "Aucune réponse du serveur, vérifiez votre connexion";
            }

            showNotification("error", errorMessage);
        }
    };

    const handleNext = () => setStep(2);

    const handleDownloadXsl = async () => {
        try {
            showNotification("loading", "Téléchargement en cours...");

            const accountId = await secureStorage.getItem("selectedAccount");
            const currentDate = new Date().toISOString();

            const response = await api.get("/exportCatalog", {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-last-request': currentDate,
                }
            });

            const downloadUrl = response.data.file_url;
            if (!downloadUrl) {
                showNotification("error", "Lien de téléchargement introuvable");
                return;
            }

            setExportedFileUrl(downloadUrl);

            const filename = downloadUrl.split('/').pop()?.split('?')[0] || 'catalogue.xlsx';
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            hideNotification();
        } catch (error) {
            console.error("Erreur lors du téléchargement du modèle:", error);
            showNotification("error", "Erreur lors du téléchargement du modèle");
        }
    };

    /* ── Tabs that use the full-page "Nouveau" link ── */
    const newRouteMap = {
        articles: "/NewProductForm",
        produits: "/NewProductForm",
        prestations: "/NewProductForm",
        ingredients: "/NewProductForm",
        categories: "/NewCategoryForm",
        brands: "/NewBrandForm",
        remises: "/NewRemiseForm",
    };
    const showNewButton = !!newRouteMap[activeTab];
    const showNewMenuButton = activeTab === "menus";


    return (
        <div className="h-screen bg-white dark:bg-gray-900">
            {/* Header */}
            <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <div className="px-4 py-4 sm:px-8 relative">
                    <div className="page-header-row">
                        <h1 className="page-title">Catalogue</h1>

                        {/* Header actions: plain teal buttons, same as every page header */}
                        <div className="flex items-center gap-1 sm:gap-2">
                            {(activeTab === "articles" || activeTab === "produits" || activeTab === "prestations") && (
                                <Button variant="plain" icon={Upload} compact onClick={handleOpenPopup}>Importer</Button>
                            )}

                            {showNewButton && (
                                <Button as={Link} to={newRouteMap[activeTab]} state={{returnTo: location.pathname + location.search}} variant="plain" icon={Plus} compact>
                                    Nouveau
                                </Button>
                            )}

                            {showNewMenuButton && (
                                <Button variant="plain" icon={Plus} compact onClick={() => setMenuCreateRequestKey(prev => prev + 1)}>
                                    Nouveau
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Import Popup — same as before */}
                    {isPopupVisible && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                            {step === 1 ? (
                                <div
                                    className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-[500px] mx-4 overflow-hidden">
                                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                        <h2 className="text-lg font-medium text-center text-gray-800 dark:text-white">
                                            Comment souhaitez-vous importer ?
                                        </h2>
                                    </div>
                                    <div className="px-6 py-4 space-y-3">
                                        {[
                                            {
                                                value: 'modify',
                                                title: 'Modifier le catalogue existant',
                                                desc: "Cette action permet de modifier les articles existants et d'en ajouter de nouveaux au catalogue."
                                            },
                                            {
                                                value: 'overwrite',
                                                title: 'Écraser le catalogue existant',
                                                desc: "Cette action permet d'écraser le catalogue existant, ce qui aura effet de supprimer tous les articles et de les remplacer."
                                            },
                                        ].map(opt => (
                                            <label key={opt.value}
                                                   className="block border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:border-primary transition overflow-hidden"
                                                   onClick={() => setImportOption(opt.value)}>
                                                <div className="flex items-start p-4">
                                                    <div className="mr-3 mt-1">
                                                        <div
                                                            className={`w-5 h-5 rounded-full flex items-center justify-center ${importOption === opt.value ? 'bg-primary' : 'border border-gray-300 dark:border-gray-500'}`}>
                                                            {importOption === opt.value && (
                                                                <svg xmlns="http://www.w3.org/2000/svg"
                                                                     className="h-3 w-3 text-white" viewBox="0 0 20 20"
                                                                     fill="currentColor">
                                                                    <path fillRule="evenodd"
                                                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                          clipRule="evenodd"/>
                                                                </svg>
                                                            )}
                                                        </div>
                                                        <input type="radio" name="importOption" className="hidden"
                                                               checked={importOption === opt.value}
                                                               onChange={() => setImportOption(opt.value)}/>
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-800 dark:text-white">{opt.title}</p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">{opt.desc}</p>
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
                                        <button
                                            className="py-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                                            onClick={handleClosePopup}>Annuler
                                        </button>
                                        <button
                                            className="py-4 text-primary font-medium border-l border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                                            onClick={handleNext}>Suivant
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-[500px] mx-4 overflow-hidden">
                                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                        <h2 className="text-lg font-medium text-center text-gray-800 dark:text-white">Importer
                                            mon fichier catalogue</h2>
                                    </div>
                                    <div className="px-6 py-4">
                                        <button onClick={handleDownloadXsl} className="btn-outline w-full">
                                            Télécharger le modèle de fichier catalogue
                                        </button>
                                        {exportedFileUrl && (
                                            <a
                                                href={exportedFileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                download
                                                className="mt-2 flex items-center justify-center gap-1.5 w-full text-center text-sm text-primary hover:underline"
                                            >
                                                <Download className="w-4 h-4 flex-shrink-0"/>
                                                Cliquez ici pour télécharger le catalogue
                                            </a>
                                        )}
                                        <div
                                            className="mt-6 p-4 border-dashed border-2 border-gray-300 dark:border-gray-600 rounded-md text-center hover:border-primary cursor-pointer"
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                e.currentTarget.classList.add('border-primary');
                                            }}
                                            onDragLeave={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                e.currentTarget.classList.remove('border-primary');
                                            }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                e.currentTarget.classList.remove('border-primary');
                                                const files = e.dataTransfer.files;
                                                if (files && files.length > 0) setSelectedFile(files[0]);
                                            }}
                                            onClick={() => {
                                                if (!selectedFile) document.getElementById('catalog-file-input')?.click();
                                            }}
                                        >
                                            <input type="file" id="catalog-file-input" className="hidden"
                                                   accept=".xls,.xlsx,.csv,.zip" onChange={(e) => {
                                                if (e.target.files?.length > 0) setSelectedFile(e.target.files[0]);
                                            }}/>
                                            {!selectedFile ? (
                                                <>
                                                    <svg className="w-12 h-12 mx-auto text-gray-500 dark:text-gray-400"
                                                         xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                         fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path strokeLinecap="round" strokeLinejoin="round"
                                                              d="M12 12v8m0-8l-4 4m4-4l4 4M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z"/>
                                                    </svg>
                                                    <p className="text-gray-700 dark:text-gray-300 font-semibold">Glisser
                                                        mon fichier catalogue ici</p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Vous pouvez
                                                        également le sélectionner directement</p>
                                                    <p className="text-xs text-gray-400 mt-2">Formats acceptés : Excel
                                                        (.xlsx, .xls), CSV (.csv) ou ZIP (.zip)</p>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-center">
                                                    <div
                                                        className="flex items-center justify-center w-12 h-12 mx-auto mb-2 bg-primary/10 rounded-full">
                                                        <svg className="w-6 h-6 text-primary"
                                                             xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                             fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                                        </svg>
                                                    </div>
                                                    <p className="text-gray-700 dark:text-gray-300 font-semibold">{selectedFile.name}</p>
                                                    <button className="mt-2 text-sm text-red-500 hover:text-red-700"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedFile(null);
                                                                document.getElementById('catalog-file-input').value = '';
                                                            }}>Supprimer
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {importErrors.length > 0 && (
                                            <div
                                                className="mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                                                <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">
                                                    Erreurs détectées ({importErrors.length}) :
                                                </p>
                                                <ul className="text-xs text-red-600 dark:text-red-400 space-y-1 max-h-32 overflow-y-auto">
                                                    {importErrors.map((err, index) => {
                                                        const line = err?.row ?? err?.line ?? err?.ligne;
                                                        const text = typeof err === 'string'
                                                            ? err
                                                            : (err?.message || err?.error || JSON.stringify(err));
                                                        return (
                                                            <li key={index}
                                                                className="bg-white/60 dark:bg-red-900/30 px-2 py-1 rounded">
                                                                {line != null ? `Ligne ${line} : ${text}` : text}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
                                        <button
                                            className="py-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                                            onClick={handleClosePopup}>Annuler
                                        </button>
                                        <button
                                            className="py-4 text-primary font-medium border-l border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                                            onClick={handleImportFile}>Importer
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div
                    className="flex overflow-x-auto no-scrollbar px-2 sm:px-4 border-b border-gray-200 dark:border-gray-700 md:justify-center">
                    {[
                        {key: "articles", label: "Articles", Icon: List},
                        {key: "produits", label: "Produits", Icon: Package},
                        {key: "prestations", label: "Prestations", Icon: Wrench},
                        ...(isRestaurant ? [{key: "ingredients", label: "Ingrédients", Icon: Wheat}] : []),
                        {key: "categories", label: "Catégories", Icon: Tag},
                        {key: "brands", label: "Marques", Icon: BookMarked},
                        {key: "remises", label: "Remises", Icon: DollarSign},
                        // {key: "taxes", label: "Taxes", Icon: Receipt},
                        // {key: "units", label: "Unités", Icon: Ruler},
                        {key: "menus", label: isRestaurant ? "Menus" : "Packs", Icon: BookOpen},
                    ].map(({key, label, Icon}) => (
                        <button
                            key={key}
                            className={tabClass(activeTab === key)}
                            onClick={() => setActiveTab(key)}
                        >
                            <Icon className={tabIconClass}/>
                            {label}
                        </button>
                    ))}
                </div>
            </header>

            {/* Content */}
            <div className="p-0">
                {activeTab === "articles" && <ProductList/>}
                {activeTab === "produits" && <ProductList stockableFilter="true"/>}
                {activeTab === "prestations" && <ProductList stockableFilter="false"/>}
                {activeTab === "ingredients" && isRestaurant && <ProductList ingredientFilter="true"/>}
                {activeTab === "categories" && <Categories/>}
                {activeTab === "brands" && <BrandsList/>}
                {activeTab === "remises" && <RemiseCategories/>}
                {/*{activeTab === "taxes" && <TaxesSettings/>}*/}
                {/*{activeTab === "units" && <UnitsSettings/>}*/}
                {activeTab === "menus" &&
                    <MenuPage createRequestKey={menuCreateRequestKey} isRestaurant={isRestaurant}/>}
            </div>

            {/* Notification toast */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />

            <style>{`
                .animate-in {
                    animation: slideDown 0.3s ease-out forwards;
                }
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default CatalogPage;
